import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Images from "../../assets";
import { secondaryButton } from "../../components/styles/customStyles";
import { useAppDispatch } from "../../assets/hooks";
import { closePopup, setPopup } from "../../store/slices/popupSlice";
import {
  getAdminCounsellors,
  getAdminStudentById,
  getAdminStudents,
  searchAdminStudents,
  transferStudentAdvisor,
} from "../../services";

type StudentItem = {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
};

type CounsellorItem = {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role?: string;
};

const extractList = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.list)) return payload.data.list;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
};

const getDisplayName = (obj: any) => {
  if (!obj) return "";
  const fullName = `${obj?.firstName || obj?.info?.firstName || ""} ${obj?.lastName || obj?.info?.lastName || ""}`.trim();
  return fullName || obj?.name || obj?.info?.email || "Unknown";
};

const getStudentDisplayLabel = (student: StudentItem) => {
  const name = getDisplayName(student);
  return student?.email ? `${name} (${student.email})` : name;
};

const normalizeStudentItem = (raw: any): StudentItem => {
  const nestedStudent = raw?.student || raw?.user || raw?.info || {};
  const email = raw?.email || nestedStudent?.email || "";
  const derivedNameFromEmail = email ? email.split("@")[0] : "";
  const firstName = raw?.firstName || nestedStudent?.firstName || "";
  const lastName = raw?.lastName || nestedStudent?.lastName || "";
  const name = raw?.name || nestedStudent?.name || derivedNameFromEmail || "";
  return {
    _id: raw?._id || nestedStudent?._id || "",
    firstName,
    lastName,
    name,
    email,
  };
};

const sortStudentsForDisplay = (items: StudentItem[]) => {
  return [...items].sort((a, b) => {
    const aDisplay = getDisplayName(a);
    const bDisplay = getDisplayName(b);
    const aUnknown = aDisplay === "Unknown";
    const bUnknown = bDisplay === "Unknown";

    if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
    if (aDisplay !== bDisplay) return aDisplay.localeCompare(bDisplay);
    return (a.email || "").localeCompare(b.email || "");
  });
};

const normalizeSearchText = (value: string) =>
  (value || "").toLowerCase().replace(/\s+/g, " ").trim();

const advancedStudentFilter = (options: StudentItem[], inputValue: string) => {
  const query = normalizeSearchText(inputValue);
  if (!query) return options.slice(0, 100);

  const scored = options
    .map((option) => {
      const fullName = normalizeSearchText(
        `${option?.firstName || ""} ${option?.lastName || ""}`
      );
      const name = normalizeSearchText(option?.name || "");
      const email = normalizeSearchText(option?.email || "");

      let score = 0;

      if (fullName.startsWith(query)) score += 120;
      if (name.startsWith(query)) score += 100;
      if (email.startsWith(query)) score += 90;

      if (fullName.includes(query)) score += 70;
      if (name.includes(query)) score += 60;
      if (email.includes(query)) score += 50;

      const words = fullName.split(" ").filter(Boolean);
      if (words.some((word) => word.startsWith(query))) score += 40;

      return { option, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.option);

  return scored.slice(0, 100);
};

const getStudentDetailsFromResponse = (response: any) => {
  return (
    response?.data?.student ||
    response?.student ||
    response?.data?.data ||
    response?.data ||
    null
  );
};

const getAssignedCounsellorFromStudent = (student: any) => {
  return student?.counsellor || student?.counselor || student?.advisor || null;
};

const getCounsellorById = (allCounsellors: CounsellorItem[], id: string) => {
  return allCounsellors.find((item) => item?._id === id) || null;
};

const getAssignedCounsellorIdFromStudent = (student: any) => {
  if (!student) return "";
  if (typeof student?.counsellor === "string") return student.counsellor;
  if (typeof student?.counselor === "string") return student.counselor;
  if (typeof student?.advisor === "string") return student.advisor;
  return (
    student?.counsellorId ||
    student?.counselorId ||
    student?.advisorId ||
    student?.assignedCounsellorId ||
    student?.assignedAdvisorId ||
    ""
  );
};

const getAssignedCounsellorsFromStudent = (student: any, allCounsellors: CounsellorItem[]) => {
  if (!student) return [];
  const uniqueMap = new Map<string, CounsellorItem>();
  const pushCounsellor = (raw: any) => {
    if (!raw) return;
    if (typeof raw === "string") {
      const matched = getCounsellorById(allCounsellors, raw);
      uniqueMap.set(raw, matched || { _id: raw, name: "Assigned counsellor", role: "counsellor" });
      return;
    }
    if (raw?._id) {
      uniqueMap.set(raw._id, {
        _id: raw._id,
        firstName: raw.firstName,
        lastName: raw.lastName,
        name: raw.name,
        role: raw.role || "counsellor",
      });
    }
  };

  const directValues = [
    student?.counsellor,
    student?.counselor,
    student?.advisor,
  ];
  directValues.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach(pushCounsellor);
    } else {
      pushCounsellor(value);
    }
  });

  const idCandidates = [
    student?.counsellorId,
    student?.counselorId,
    student?.advisorId,
    student?.assignedCounsellorId,
    student?.assignedAdvisorId,
    student?.counsellorIds,
    student?.counselorIds,
    student?.advisorIds,
    student?.assignedCounsellorIds,
    student?.assignedAdvisorIds,
  ];
  idCandidates.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach(pushCounsellor);
    } else {
      pushCounsellor(value);
    }
  });

  // New shape from single-student API:
  // advisors: [{ _id: <assignmentId>, info: { _id, firstName, lastName, email } }]
  const advisorsList = Array.isArray(student?.advisors) ? student.advisors : [];
  advisorsList.forEach((advisorEntry: any) => {
    const info = advisorEntry?.info || {};
    const advisorId = info?._id || advisorEntry?._id || "";
    if (!advisorId) return;
    const matched = getCounsellorById(allCounsellors, advisorId);
    uniqueMap.set(advisorId, {
      _id: advisorId,
      firstName: info?.firstName || matched?.firstName,
      lastName: info?.lastName || matched?.lastName,
      name:
        `${info?.firstName || ""} ${info?.lastName || ""}`.trim() ||
        info?.email ||
        matched?.name ||
        "Assigned counsellor",
      role: matched?.role || "counsellor",
    });
  });

  return Array.from(uniqueMap.values());
};

const ChangeAdvisor = () => {
  const dispatch = useAppDispatch();
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [allStudents, setAllStudents] = useState<StudentItem[]>([]);
  const [counsellors, setCounsellors] = useState<CounsellorItem[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentItem | null>(null);
  const [studentSearchText, setStudentSearchText] = useState("");
  const [selectedCounsellor, setSelectedCounsellor] = useState<CounsellorItem | null>(null);
  const [fromCounsellor, setFromCounsellor] = useState<CounsellorItem | null>(null);
  const [fromCounsellorOptions, setFromCounsellorOptions] = useState<CounsellorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [counsellorsLoading, setCounsellorsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const selectedStudentId = selectedStudent?._id || "";
  const fromId = fromCounsellor?._id || "";
  const toId = selectedCounsellor?._id || "";
  const role = fromCounsellor?.role || "counsellor";

  const selectedCounsellorOptions = useMemo(
    () => counsellors.filter((item) => item?._id !== fromId),
    [counsellors, fromId]
  );

  const showError = (message: string, width = "350px") => {
    dispatch(
      setPopup({
        show: true,
        data: {
          container: {
            name: "error",
            data: message,
            dimensions: { width },
          },
          type: "custom",
        },
      })
    );
  };

  const fetchInitialData = async () => {
    setLoading(true);
    setCounsellorsLoading(true);
    try {
      const [studentsResponse, counsellorsResponse] = await Promise.all([
        getAdminStudents(),
        getAdminCounsellors(),
      ]);
      const rawStudents = extractList(studentsResponse);
      const normalizedStudents = rawStudents
        .map(normalizeStudentItem)
        .filter((item) => Boolean(item._id));
      const sortedStudents = sortStudentsForDisplay(normalizedStudents);
      setStudents(sortedStudents);
      setAllStudents(sortedStudents);
      setCounsellors(extractList(counsellorsResponse));
    } catch {
      showError("Unable to load students/counsellors. Please retry.");
    } finally {
      setLoading(false);
      setCounsellorsLoading(false);
    }
  };

  const refreshCounsellors = async () => {
    setCounsellorsLoading(true);
    try {
      const counsellorsResponse = await getAdminCounsellors();
      setCounsellors(extractList(counsellorsResponse));
    } catch {
      showError("Unable to refresh counsellors. Please retry.");
    } finally {
      setCounsellorsLoading(false);
    }
  };

  const fetchSelectedStudentDetails = async (studentId: string) => {
    setDetailsLoading(true);
    try {
      const response = await getAdminStudentById(studentId);
      const student = getStudentDetailsFromResponse(response);
      const assignedCounsellors = getAssignedCounsellorsFromStudent(student, counsellors);
      if (assignedCounsellors.length > 0) {
        setFromCounsellorOptions(assignedCounsellors);
        setFromCounsellor(assignedCounsellors[0]);
        return;
      }

      // Legacy fallback for uncommon shapes.
      const assignedCounsellor = getAssignedCounsellorFromStudent(student);
      if (assignedCounsellor?._id) {
        const normalized = {
          _id: assignedCounsellor._id,
          firstName: assignedCounsellor.firstName,
          lastName: assignedCounsellor.lastName,
          name: assignedCounsellor.name,
          role: assignedCounsellor.role || "counsellor",
        };
        setFromCounsellorOptions([normalized]);
        setFromCounsellor(normalized);
        return;
      }

      const assignedCounsellorId = getAssignedCounsellorIdFromStudent(student);
      if (assignedCounsellorId) {
        const matchedCounsellor = getCounsellorById(counsellors, assignedCounsellorId) || {
          _id: assignedCounsellorId,
          name: "Assigned counsellor",
          role: "counsellor",
        };
        setFromCounsellorOptions([matchedCounsellor]);
        setFromCounsellor(matchedCounsellor);
        return;
      }

      setFromCounsellorOptions([]);
      setFromCounsellor(null);
      showError("No assigned counsellor found for this student.");
    } catch {
      setFromCounsellorOptions([]);
      setFromCounsellor(null);
      showError("Unable to fetch selected student details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const query = studentSearchText.trim();
    if (!query) {
      setStudents(allStudents);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await searchAdminStudents(query);
        const normalizedStudents = extractList(response)
          .map(normalizeStudentItem)
          .filter((item) => Boolean(item._id));

        if (normalizedStudents.length > 0) {
          setStudents(sortStudentsForDisplay(normalizedStudents));
        } else {
          setStudents(advancedStudentFilter(allStudents, query));
        }
      } catch {
        setStudents(advancedStudentFilter(allStudents, query));
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [studentSearchText, allStudents]);

  const handleStudentChange = async (_: any, value: StudentItem | null) => {
    setSelectedStudent(value);
    setStudentSearchText(value ? getStudentDisplayLabel(value) : "");
    setSelectedCounsellor(null);
    setFromCounsellor(null);
    setFromCounsellorOptions([]);
    if (value?._id) {
      await fetchSelectedStudentDetails(value._id);
    }
  };

  const handleTransfer = async () => {
    if (!selectedStudentId) {
      showError("Please select a student first.", "300px");
      return;
    }
    if (!fromId) {
      showError("Current counsellor not found for selected student.");
      return;
    }
    if (!toId) {
      showError("Please select the new counsellor.", "320px");
      return;
    }
    if (fromId === toId) {
      showError("Current and new counsellor cannot be same.");
      return;
    }

    setIsSubmitting(true);
    try {
      await transferStudentAdvisor({
        studentId: selectedStudentId,
        fromId,
        toId,
        role,
      });

      // Verify transfer by reading student details again.
      const verifyResponse = await getAdminStudentById(selectedStudentId);
      const updatedStudent = getStudentDetailsFromResponse(verifyResponse);
      const updatedAssigned = getAssignedCounsellorsFromStudent(updatedStudent, counsellors);
      const updatedIds = updatedAssigned.map((item) => item._id);

      if (updatedIds.includes(toId)) {
        showError("Student advisor changed successfully.");
      } else {
        showError("Transfer request submitted, but verification is pending. Please refresh once.");
      }
      dispatch(closePopup());
    } catch {
      showError("Transfer failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} mb={2}>
        Change Advisor
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            options={students}
            value={selectedStudent}
            inputValue={studentSearchText}
            loading={loading || isSearching}
            onChange={handleStudentChange}
            onInputChange={(_, value, reason) => {
              setStudentSearchText(value);
              if (reason === "input") {
                setSelectedStudent(null);
                setSelectedCounsellor(null);
                setFromCounsellor(null);
                setFromCounsellorOptions([]);
              }
            }}
            openOnFocus
            filterOptions={(options, state) =>
              advancedStudentFilter(options, state.inputValue)
            }
            getOptionLabel={(option) => getStudentDisplayLabel(option)}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
                <Typography fontSize="0.9rem" fontWeight={500}>
                  {getDisplayName(option)}
                </Typography>
                <Typography fontSize="0.78rem" color="#666">
                  {option?.email || "Email not available"}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Search Student"
                placeholder="Type student name"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading || isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Selected Student Email"
            value={selectedStudent?.email || "Not available"}
            InputProps={{ readOnly: true }}
          />
        </Grid>

        <Grid item xs={12}>
          {fromCounsellorOptions.length > 1 ? (
            <Autocomplete
              options={fromCounsellorOptions}
              value={fromCounsellor}
              onChange={(_, value) => setFromCounsellor(value)}
              getOptionLabel={(option) => `${getDisplayName(option)} (${option?.role || "counsellor"})`}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Current Counsellor (Select from assigned)"
                  placeholder={detailsLoading ? "Loading..." : "Choose current counsellor"}
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Current Counsellor"
              value={detailsLoading ? "Loading..." : getDisplayName(fromCounsellor) || "Not available"}
              InputProps={{ readOnly: true }}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            options={selectedCounsellorOptions}
            value={selectedCounsellor}
            loading={counsellorsLoading}
            onOpen={refreshCounsellors}
            onChange={(_, value) => setSelectedCounsellor(value)}
            getOptionLabel={(option) => getDisplayName(option)}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="New Counsellor"
                placeholder="Select counsellor"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {counsellorsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Button
            sx={[secondaryButton, { width: "170px" }]}
            onClick={handleTransfer}
            disabled={!selectedStudentId || !fromId || !toId || isSubmitting || detailsLoading}
          >
            {isSubmitting ? (
              <img
                src={Images.standardLoader}
                alt="loading"
                style={{ width: "20px", height: "20px", padding: "5px" }}
              />
            ) : (
              "Submit Change"
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChangeAdvisor;
